/**
 * React Query hooks + mutations for the EDR Hunts engine and Replay job
 * runner. All endpoints live under /api/v1/connectors/edr/admin/*.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { edrDelete, edrGet, edrPost } from "@/services/edrApi";
import { useConnectorEnabled } from "@/hooks/connectors/useConnector";
import type {
  HuntDef,
  HuntListResponse,
  HuntSearchResponse,
  HuntValidateResponse,
  ReplayJob,
  ReplayJobListResponse,
} from "@/types/edrHunts";

const BASE = "/api/v1/connectors/edr/admin";

function asArray<T>(r: { hunts?: T[]; jobs?: T[]; data?: T[] } | null | undefined): T[] {
  if (!r) return [];
  return r.hunts ?? r.jobs ?? r.data ?? [];
}

// ---------- Saved hunts ----------

export function useHunts() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "hunts", "list"],
    staleTime: 30_000,
    queryFn: async () => {
      const res = await edrGet<HuntListResponse>(`${BASE}/hunts`);
      return asArray<HuntDef>(res);
    },
  });
}

export function useHunt(id: string | undefined) {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled: enabled && !!id,
    queryKey: ["edr", "hunts", "one", id],
    queryFn: async () => edrGet<HuntDef>(`${BASE}/hunts/${id}`),
  });
}

export function useSaveHunt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<HuntDef> & { queryDsl: string; name: string }) => {
      return edrPost<HuntDef>(`${BASE}/hunts`, payload);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["edr", "hunts", "list"] }),
  });
}

export function useDeleteHunt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => edrDelete<void>(`${BASE}/hunts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["edr", "hunts", "list"] }),
  });
}

// ---------- Synchronous search + validate ----------

export function useValidateHunt() {
  return useMutation({
    mutationFn: async (queryDsl: string): Promise<HuntValidateResponse> => {
      return edrPost<HuntValidateResponse>(`${BASE}/hunts/validate`, { queryDsl });
    },
  });
}

export function useRunHunt() {
  return useMutation({
    mutationFn: async (input: { queryDsl: string; windowHours: number; limit?: number }) => {
      return edrPost<HuntSearchResponse>(`${BASE}/hunts/search`, {
        queryDsl: input.queryDsl,
        windowHours: input.windowHours,
        limit: input.limit ?? 500,
      });
    },
  });
}

/** Lazy hit-count for a single saved hunt (limit:0 → cheap count). */
export function useHuntCount(huntId: string | undefined, queryDsl: string, windowHours: number) {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled: enabled && !!huntId && !!queryDsl,
    queryKey: ["edr", "hunts", "count", huntId, windowHours],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await edrPost<HuntSearchResponse>(`${BASE}/hunts/search`, {
        queryDsl,
        windowHours,
        limit: 0,
      });
      return res?.matchCount ?? 0;
    },
    retry: false,
  });
}

// ---------- Replay jobs ----------

export function useReplayJobs(pollMs = 0) {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "replay-jobs", "list"],
    staleTime: 5_000,
    refetchInterval: pollMs || false,
    queryFn: async () => {
      const res = await edrGet<ReplayJobListResponse>(`${BASE}/replay-jobs`);
      return asArray<ReplayJob>(res);
    },
  });
}

export function useReplayJob(id: string | undefined, poll = true) {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled: enabled && !!id,
    queryKey: ["edr", "replay-jobs", "one", id],
    refetchInterval: (q) => {
      if (!poll) return false;
      const status = (q.state.data as ReplayJob | undefined)?.status;
      if (status === "complete" || status === "failed" || status === "cancelled") return false;
      return 2_000;
    },
    queryFn: async () => edrGet<ReplayJob>(`${BASE}/replay-jobs/${id}`),
  });
}

export function useSubmitAdhocReplay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { queryDsl: string; windowHours: number }) => {
      return edrPost<ReplayJob>(`${BASE}/hunts/replay/adhoc`, input);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["edr", "replay-jobs", "list"] }),
  });
}

export function useReplaySavedHunt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; windowHours: number }) => {
      return edrPost<ReplayJob>(`${BASE}/hunts/${input.id}/replay`, {
        windowHours: input.windowHours,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["edr", "replay-jobs", "list"] }),
  });
}

export function useReplaySigmaRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; windowHours: number }) => {
      return edrPost<ReplayJob>(`${BASE}/sigma-rules/${input.id}/replay`, {
        windowHours: input.windowHours,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["edr", "replay-jobs", "list"] }),
  });
}

export function useCancelReplayJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => edrPost<void>(`${BASE}/replay-jobs/${id}/cancel`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["edr", "replay-jobs"] }),
  });
}

export function useDeleteReplayJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => edrDelete<void>(`${BASE}/replay-jobs/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["edr", "replay-jobs", "list"] }),
  });
}

// ---------- Starter hunt catalog ----------

import type { StarterHunt } from "@/components/security/hunts/starterHunts";

export function useStarterHunts(fallback: StarterHunt[]) {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "starter-hunts"],
    staleTime: 60 * 60_000,
    queryFn: async () => {
      try {
        const res = await edrGet<{ hunts?: StarterHunt[]; data?: StarterHunt[] }>(
          "/api/v1/connectors/edr/research/starter-hunts",
        );
        const rows = res?.hunts ?? res?.data ?? [];
        return rows.length > 0 ? rows : fallback;
      } catch {
        return fallback;
      }
    },
    retry: false,
  });
}
